import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useSnackbar } from "notistack";
import moment from "moment";
import {
  saveReviewPlanApi,
  skipCheckInApi,
  skipReviewApi,
  unskipCheckInApi,
} from "src/DAL/coachWorkspace/coachWorkspace";
import { CW, Eyebrow, InfoHint, Pill, TextLink, WEEKDAYS } from "./cwUi";

/**
 * P3.9 — plan / skip (design PDF page 8, same layout): left "Two dates with
 * different jobs" (client check-in + coach review plan + existing review),
 * right "Skip one upcoming check-in" (or "Skip this review").
 *   Plan review  — the coach's own review day/time for this client (planner).
 *   Skip review  — this check-in needs no review/feedback (kept in history).
 *   Skip check-in — an upcoming/open check-in is excused (holiday, illness…):
 *                   no reminders, not counted as missed. Can be undone.
 */

const SKIP_REASONS = ["Holiday", "Illness / injury", "Event / travel", "Coach decision", "Other"];

const fieldSx = { "& .MuiOutlinedInput-root": { borderRadius: "10px", bgcolor: "#fff" } };


/**
 * DIALOG-CLOSE (3 Nov): close only with ✕ / Cancel. A click outside, or the
 * Escape / Cancel of the browser's own time or date picker, used to close
 * this dialog too (the coach saw two popups disappear at once).
 */
const safeClose = (onClose, busy) => (event, reason) => {
  if (busy || reason === "backdropClick") return;
  if (reason === "escapeKeyDown") {
    const el = typeof document !== "undefined" ? document.activeElement : null;
    if (el && /^(INPUT|SELECT|TEXTAREA)$/.test(el.tagName)) return;
  }
  if (onClose) onClose();
};

export default function PlanSkipDialog({
  open,
  onClose,
  initialTab = "plan",
  submissionId,
  trackerId,
  personKey,
  memberId,
  personName,
  reply,
  schedule,
  onChangeDay,
  onSaved,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [right, setRight] = useState("skipCheckIn"); // "skipCheckIn" | "skipReview"
  const [weekday, setWeekday] = useState("");
  const [time, setTime] = useState("");
  const [skipDate, setSkipDate] = useState("");
  const [reason, setReason] = useState(SKIP_REASONS[0]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const planRef = React.useRef(null);

  const recurring = Boolean(schedule);

  useEffect(() => {
    if (!open) return;
    setRight(initialTab === "skipReview" || !recurring ? "skipReview" : "skipCheckIn");
    setWeekday(schedule?.reviewPlan?.weekday ?? "");
    setTime(schedule?.reviewPlan?.time || "");
    const first = (schedule?.upcoming || []).find((u) => !u.skipped && u.state !== "open") || (schedule?.upcoming || []).find((u) => !u.skipped);
    setSkipDate(first?.opens || "");
    setReason(SKIP_REASONS[0]);
    setNote("");
    setError("");
  }, [open, initialTab, schedule, recurring]);

  const done = (result, fallback, close = true) => {
    setBusy(false);
    if (result?.code === 200) {
      enqueueSnackbar(result.message || fallback, { variant: "success" });
      if (onSaved) onSaved(result);
      if (close) onClose();
    } else setError(result?.message || "Could not save");
  };

  const savePlan = async () => {
    setBusy(true);
    done(
      await saveReviewPlanApi({ trackerId, personKey, memberId, weekday: weekday === "" ? null : Number(weekday), time }),
      "Review plan saved"
    );
  };
  const skipReview = async () => {
    setBusy(true);
    done(await skipReviewApi(submissionId, note ? `${reason}: ${note}` : reason), "Review skipped");
  };
  const skipCheckIn = async () => {
    setBusy(true);
    done(await skipCheckInApi({ trackerId, personKey, memberId, date: skipDate, reason, note }), "Check-in skipped");
  };
  const unskip = async (skipId) => {
    setBusy(true);
    done(await unskipCheckInApi({ trackerId, personKey, memberId, skipId }), "Skip removed");
  };

  const upcoming = schedule?.upcoming || [];
  const nextWindow = schedule?.next || upcoming.find((u) => u.state === "upcoming" && !u.skipped) || null;
  const first = String(personName || "").split(" ")[0] || "This client";
  const selected = upcoming.find((u) => u.opens === skipDate);
  const after = selected ? upcoming.find((u) => !u.skipped && new Date(u.opens) > new Date(selected.opens)) : null;
  const card = { bgcolor: "#fff", border: `1px solid ${CW.line}`, borderRadius: "14px", p: { xs: 2, md: 3 }, minWidth: 0 };
  const h = { fontSize: { xs: 19, md: 23 }, fontWeight: 700, color: CW.ink };

  return (
    <Dialog open={open} onClose={safeClose(onClose, busy)} fullWidth maxWidth="lg" PaperProps={{ sx: { bgcolor: CW.soft, borderRadius: "16px" } }}>
      <DialogTitle sx={{ pb: 0.5, pr: 7, position: "relative" }}>
        <Typography sx={{ fontSize: { xs: 20, md: 24 }, fontWeight: 700, color: CW.ink }}>Plan or skip · {personName || "Client"}</Typography>
        <Typography sx={{ fontSize: 14, color: CW.muted, mt: 0.25 }}>Review plan and check-in skips for this client only</Typography>
        <IconButton
          aria-label="Close"
          data-testid="dialog-close"
          onClick={busy ? undefined : onClose}
          disabled={busy}
          sx={{ position: "absolute", right: 12, top: 12, color: CW.muted }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: "16px !important" }}>
        {error ? (
          <Alert severity="error" sx={{ mb: 1.5 }}>
            {error}
          </Alert>
        ) : null}
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2.5, alignItems: "start" }}>
          {/* LEFT — check-in day and review plan */}
          <Box sx={card}>
            <Typography sx={h}>
              Check-in day and review plan
              <InfoHint id="planskip.left" />
            </Typography>
            {recurring ? (
              <>
                <Eyebrow sx={{ color: CW.primary, mt: 2.5 }}>Client check-in</Eyebrow>
                <Typography sx={{ fontSize: { xs: 18, md: 21 }, fontWeight: 700, color: CW.ink, mt: 0.75 }}>
                  {schedule.current?.label || schedule.rules?.cadenceLabel || "—"}
                </Typography>
                <Typography sx={{ fontSize: 13.5, color: CW.muted, mt: 1 }}>
                  {nextWindow?.opensLabel ? `The client's next window opens ${nextWindow.opensLabel}.` : "No upcoming window."}
                </Typography>
                {onChangeDay ? (
                  <TextLink onClick={onChangeDay} sx={{ mt: 1.5 }}>
                    Change check-in day and time →
                  </TextLink>
                ) : null}

                <Box ref={planRef} sx={{ borderTop: `1px solid ${CW.line}`, mt: 2.5, pt: 2.5 }}>
                  <Eyebrow sx={{ color: CW.primary }}>Coach review plan</Eyebrow>
                  <Typography sx={{ fontSize: { xs: 17, md: 20 }, fontWeight: 700, color: CW.ink, mt: 0.75, mb: 1.5 }}>
                    When would you like to review?
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
                    <TextField
                      select
                      size="small"
                      value={weekday}
                      onChange={(e) => setWeekday(e.target.value)}
                      SelectProps={{ displayEmpty: true }}
                      inputProps={{ "aria-label": "Review day" }}
                      sx={{ minWidth: 200, flex: 1, ...fieldSx }}
                    >
                      <MenuItem value="">No plan</MenuItem>
                      {WEEKDAYS.map((name, i) => (
                        <MenuItem key={name} value={i}>
                          {name}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      size="small"
                      type="time"
                      value={time}
                      onChange={(e) => setTime(e.target.value)}
                      inputProps={{ "aria-label": "Review time" }}
                      sx={{ width: 150, ...fieldSx }}
                    />
                    <Typography sx={{ fontSize: 13, color: CW.muted }}>your time</Typography>
                  </Box>
                  <Typography sx={{ fontSize: 13, color: CW.muted, mt: 1.5 }}>
                    Applies to new review tasks. Existing reviews keep their dates.
                    <br />
                    The reply deadline still follows the actual submission.
                  </Typography>
                  <button type="button" className="small-contained-button" style={{ padding: "10px 28px", marginTop: 16 }} disabled={busy} onClick={savePlan}>
                    Save review plan
                  </button>
                </Box>
              </>
            ) : (
              <Typography sx={{ fontSize: 13.5, color: CW.muted, mt: 1.5 }}>
                This journey has no repeating check-in, so there is no check-in day or review plan.
              </Typography>
            )}

            {submissionId && reply ? (
              <Box sx={{ bgcolor: "#fff1e6", borderRadius: "12px", p: 2.25, mt: 3 }}>
                <Typography sx={{ fontSize: 17, fontWeight: 700, color: CW.ink }}>
                  Existing review · {moment(reply.submittedAt).format("D MMMM")}
                </Typography>
                <Typography sx={{ fontSize: 14.5, fontWeight: 700, color: reply.overdue ? CW.red : CW.ink, mt: 0.75 }}>
                  Reply due {moment(reply.reviewDueAt).format("D MMM, HH:mm")}
                  {reply.overdue ? " · Overdue" : ""}
                </Typography>
                <Typography sx={{ fontSize: 12.5, color: CW.muted, mt: 1 }}>Moving its planned slot does not extend this deadline.</Typography>
                <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, mt: 2, flexWrap: "wrap" }}>
                  {recurring ? (
                    <TextLink onClick={() => planRef.current && planRef.current.scrollIntoView({ behavior: "smooth", block: "center" })}>
                      Move planned review…
                    </TextLink>
                  ) : (
                    <span />
                  )}
                  <TextLink onClick={() => setRight("skipReview")}>Skip this review…</TextLink>
                </Box>
              </Box>
            ) : null}
          </Box>

          {/* RIGHT — skip one upcoming check-in / skip this review */}
          <Box sx={card}>
            {right === "skipCheckIn" && recurring ? (
              <>
                <Typography sx={h}>
                  Skip one upcoming check-in
                  <InfoHint id="planskip.right" />
                </Typography>
                <Pill tone="teal" sx={{ mt: 1.5, fontSize: 12.5, px: 1.5, py: 0.6 }}>
                  {first} only · One window
                </Pill>
                <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: CW.ink, mt: 2.5, mb: 0.75 }}>Selected check-in</Typography>
                <TextField
                  select
                  fullWidth
                  value={skipDate}
                  onChange={(e) => setSkipDate(e.target.value)}
                  inputProps={{ "aria-label": "Check-in" }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px", bgcolor: CW.soft, fontWeight: 700, fontSize: 17 } }}
                >
                  {upcoming
                    .filter((u) => !u.skipped)
                    .map((u) => (
                      <MenuItem key={u.opens} value={u.opens}>
                        {u.opensLabel}
                        {u.state === "open" ? " · open now" : " · not yet open"}
                      </MenuItem>
                    ))}
                </TextField>
                <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: CW.ink, mt: 2, mb: 0.75 }}>Reason</Typography>
                <TextField select fullWidth value={reason} onChange={(e) => setReason(e.target.value)} inputProps={{ "aria-label": "Reason" }} sx={fieldSx}>
                  {SKIP_REASONS.map((r) => (
                    <MenuItem key={r} value={r}>
                      {r}
                    </MenuItem>
                  ))}
                </TextField>
                <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: CW.ink, mt: 2, mb: 0.75 }}>Note</Typography>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="e.g. Agreed week away."
                  inputProps={{ maxLength: 1000, "aria-label": "Note" }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px", bgcolor: CW.soft } }}
                />
                <Box sx={{ bgcolor: CW.tealSoft, borderRadius: "12px", p: 2.25, mt: 2.5 }}>
                  <Typography sx={{ fontSize: 17, fontWeight: 700, color: CW.ink, mb: 1 }}>What happens</Typography>
                  <Typography sx={{ fontSize: 13.5, color: CW.ink, lineHeight: 1.7 }}>
                    This window is marked Excused and its reminders stop.
                    <br />
                    {after ? `The next check-in opens ${after.opensLabel}.` : "The next check-in opens on schedule."}
                    <br />
                    Other clients and {first}'s submitted reviews stay unchanged.
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: 12.5, color: CW.muted, mt: 2 }}>This does not send feedback or mark a review completed.</Typography>
                {upcoming.some((u) => u.skipped) ? (
                  <Box sx={{ mt: 2 }}>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: CW.muted, mb: 0.5 }}>Already skipped</Typography>
                    {upcoming
                      .filter((u) => u.skipped)
                      .map((u) => (
                        <Box key={u.opens} sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5 }}>
                          <Pill>{u.opensLabel}</Pill>
                          <Typography sx={{ fontSize: 12.5, color: CW.muted, flex: 1 }}>{u.skip?.reason}</Typography>
                          <TextLink sx={{ fontSize: 12.5 }} disabled={busy} onClick={() => unskip(u.skip?.id)}>
                            Undo
                          </TextLink>
                        </Box>
                      ))}
                  </Box>
                ) : null}
                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, mt: 2.5, flexWrap: "wrap" }}>
                  <button type="button" className="small-contained-outlined-button" style={{ padding: "10px 26px", color: CW.ink, borderColor: CW.line }} onClick={onClose} disabled={busy}>
                    Cancel
                  </button>
                  <button type="button" className="small-contained-button" style={{ padding: "10px 26px", minWidth: 200 }} disabled={busy || !skipDate} onClick={skipCheckIn}>
                    Skip this check-in
                  </button>
                </Box>
              </>
            ) : (
              <>
                <Typography sx={h}>
                  Skip this review
                  <InfoHint id="planskip.right" />
                </Typography>
                <Pill tone="teal" sx={{ mt: 1.5, fontSize: 12.5, px: 1.5, py: 0.6 }}>
                  {first} only · This check-in
                </Pill>
                <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: CW.ink, mt: 2.5, mb: 0.75 }}>Reason</Typography>
                <TextField select fullWidth value={reason} onChange={(e) => setReason(e.target.value)} inputProps={{ "aria-label": "Reason" }} sx={fieldSx}>
                  {SKIP_REASONS.map((r) => (
                    <MenuItem key={r} value={r}>
                      {r}
                    </MenuItem>
                  ))}
                </TextField>
                <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: CW.ink, mt: 2, mb: 0.75 }}>Note</Typography>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  inputProps={{ maxLength: 300, "aria-label": "Note" }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px", bgcolor: CW.soft } }}
                />
                <Box sx={{ bgcolor: CW.tealSoft, borderRadius: "12px", p: 2.25, mt: 2.5 }}>
                  <Typography sx={{ fontSize: 17, fontWeight: 700, color: CW.ink, mb: 1 }}>What happens</Typography>
                  <Typography sx={{ fontSize: 13.5, color: CW.ink, lineHeight: 1.7 }}>
                    The check-in stays in history but leaves your queue.
                    <br />
                    No feedback is expected for it.
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1.5, mt: 2.5, flexWrap: "wrap" }}>
                  {recurring ? (
                    <TextLink onClick={() => setRight("skipCheckIn")} sx={{ fontSize: 13.5 }}>
                      Skip an upcoming check-in instead
                    </TextLink>
                  ) : (
                    <span />
                  )}
                  <Box sx={{ display: "flex", gap: 1.5 }}>
                    <button type="button" className="small-contained-outlined-button" style={{ padding: "10px 26px", color: CW.ink, borderColor: CW.line }} onClick={onClose} disabled={busy}>
                      Cancel
                    </button>
                    <button type="button" className="small-contained-button" style={{ padding: "10px 26px" }} disabled={busy || !submissionId} onClick={skipReview}>
                      Skip review
                    </button>
                  </Box>
                </Box>
              </>
            )}
          </Box>
        </Box>
        <Typography sx={{ fontSize: 13.5, color: CW.muted, mt: 2.5 }}>
          Group actions belong in tracker management. This screen only changes {first}.
        </Typography>
      </DialogContent>
    </Dialog>
  );
}
